// AbstractSection.js
import React from 'react';

const highlights = [
  {
    title: 'Explicit Safety Signal',
    body: (
      <>
        We add a dedicated <code className="px-1 py-0.5 rounded bg-ink-100 text-ink-800 text-xs">[CLS]</code> token
        that <strong>explicitly</strong> classifies whether the input and current
        trajectory are benign or malicious.
      </>
    ),
    pill: 'classification',
  },
  {
    title: 'Strategic Attention & Decoding',
    body: (
      <>
        The CLS prediction re-attends to query/response tokens and steers
        decoding &mdash; safety is <strong>re-evaluated per step</strong>, not
        only at the start.
      </>
    ),
    pill: 'design',
  },
  {
    title: 'Robust with Low Overhead',
    body: (
      <>
        With less than <strong>0.2x</strong> overhead cost, our method drops
        attack success rates from <strong>93% → &lt;1%</strong> on multiple
        jailbreak settings.
      </>
    ),
    pill: 'results',
  },
];

export default function AbstractSection() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">01 &middot; Overview</p>
        <h2 className="section-title">Abstract</h2>

        <div className="mt-10 prose-paper space-y-5 max-w-3xl mx-auto">
          <p>
            Recent studies on the safety alignment of large language models
            (LLMs) have revealed that existing approaches often operate
            superficially, leaving models vulnerable to various adversarial
            attacks. Despite their significance, these studies generally fail
            to offer actionable solutions beyond data augmentation for
            achieving more robust safety mechanisms.
          </p>
          <p>
            This paper identifies a fundamental cause of this superficiality:
            existing alignment approaches often presume that models can{' '}
            <em>implicitly</em> learn a safety-related reasoning task during
            the alignment process. However, the learned safety signals are
            often diluted by other competing objectives, leading models to
            struggle with drawing a firm safety-conscious decision boundary
            under adversarial pressure.
          </p>
          <p>
            By explicitly introducing a safety-related{' '}
            <strong>binary classification task</strong> and integrating its
            signals with our attention and decoding strategies, we eliminate
            this ambiguity and allow models to respond more responsibly to
            malicious queries.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {highlights.map((h) => (
            <div
              key={h.title}
              className="card p-6 hover:shadow-card hover:-translate-y-0.5 transition-all duration-200"
            >
              <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50 rounded-full px-2 py-0.5">
                {h.pill}
              </span>
              <h3 className="mt-3 text-lg font-semibold text-ink-900 font-serif">
                {h.title}
              </h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                {h.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
